import React, { useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NavigateNextIcon from '@mui/icons-material/KeyboardArrowDown';
import NavigateBeforeIcon from '@mui/icons-material/KeyboardArrowUp';

export interface SearchBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  caseInsensitive: boolean;
  onCaseInsensitiveChange: (value: boolean) => void;
  useRegex: boolean;
  onUseRegexChange: (value: boolean) => void;
  matchCount: number;
  currentMatchIndex: number;
  onNext: () => void;
  onPrev: () => void;
  compact?: boolean;
  overlay?: boolean;
  /** Renders before the search input (e.g. copy button), used when overlay */
  prefix?: React.ReactNode;
  /** When true with overlay, parent provides the overlay container; SearchBar does not self-position */
  inOverlayGroup?: boolean;
  /** When true with inOverlayGroup, do not add outer container background/border/shadow (single-bordered input only) */
  noOuterBox?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  onSearchChange,
  caseInsensitive,
  onCaseInsensitiveChange,
  useRegex,
  onUseRegexChange,
  matchCount,
  currentMatchIndex,
  onNext,
  onPrev,
  compact = false,
  overlay = false,
  prefix,
  inOverlayGroup = false,
  noOuterBox = false,
}) => {
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      if (e.shiftKey) onPrev();
      else onNext();
    },
    [onNext, onPrev]
  );

  const toggleSx = {
    m: 0,
    ml: 0,
    mr: 0,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    '& .MuiFormControlLabel-label': { lineHeight: 1, userSelect: 'none' },
    '& .MuiCheckbox-root': { py: 0, px: 0.25 },
  };

  const content = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 0.75,
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        boxSizing: 'border-box',
        flexShrink: 0,
        width: overlay && !inOverlayGroup ? 'auto' : '100%',
        ...(compact
          ? {
              minHeight: 40,
              py: 0.25,
              '& .MuiInputBase-input': { py: 0.5, fontSize: '0.875rem' },
            }
          : {}),
        ...(overlay && !inOverlayGroup
          ? {
              position: 'absolute',
              top: 6,
              left: 6,
              right: 6,
              zIndex: 10,
              backgroundColor: '#ffffff',
              borderRadius: 1,
              padding: '4px 6px',
            }
          : overlay && inOverlayGroup && !noOuterBox
          ? {
              backgroundColor: '#ffffff',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
              padding: '6px 10px',
              gap: 0.75,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1.5,
                '& fieldset': { borderColor: '#e0e0e0', borderWidth: 1 },
                '&:hover fieldset': { borderColor: '#bdbdbd' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1 },
              },
              '& .MuiInputBase-input': {
                '&::placeholder': { color: 'rgba(0,0,0,0.45)', opacity: 1 },
              },
              '& .MuiInputAdornment-root': { color: 'rgba(0,0,0,0.45)' },
              '& .MuiInputAdornment-root svg': { color: 'rgba(0,0,0,0.45)' },
              '& .MuiIconButton-root': { color: 'rgba(0,0,0,0.45)' },
              '& .MuiIconButton-root svg': { color: 'rgba(0,0,0,0.45)' },
              '& .MuiFormControlLabel-label': { color: 'rgba(0,0,0,0.6)' },
              '& .MuiCheckbox-root': { color: 'rgba(0,0,0,0.45)' },
              '& .MuiCheckbox-root.Mui-checked': { color: 'primary.main' },
              '& .MuiTypography-root': { color: 'rgba(0,0,0,0.45)' },
              '& > *:first-of-type': { color: 'rgba(0,0,0,0.45)' },
              '& > *:first-of-type svg': { color: 'rgba(0,0,0,0.45)' },
            }
          : overlay && inOverlayGroup && noOuterBox
          ? {}
          : !overlay
          ? { mb: compact ? 0 : 1 }
          : {}),
      }}
    >
      {prefix ? <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{prefix}</Box> : null}
      <TextField
        size={compact ? 'small' : 'medium'}
        placeholder={compact ? 'Search' : 'Search…'}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={onKeyDown}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: compact ? 20 : 24 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          flex: '1 1 120px',
          minWidth: 80,
          width: 'auto',
        }}
      />
      {matchCount > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          {currentMatchIndex + 1}/{matchCount}
        </Typography>
      )}
      {searchQuery.trim() && matchCount === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          No matches
        </Typography>
      )}
      <IconButton
        size="small"
        onClick={onPrev}
        title="Previous (Shift+Enter)"
        disabled={matchCount === 0}
        sx={{ flexShrink: 0 }}
      >
        <NavigateBeforeIcon />
      </IconButton>
      <IconButton
        size="small"
        onClick={onNext}
        title="Next (Enter)"
        disabled={matchCount === 0}
        sx={{ flexShrink: 0 }}
      >
        <NavigateNextIcon />
      </IconButton>
      {!compact && (
        <>
          <FormControlLabel
            sx={toggleSx}
            control={
              <Checkbox
                size="small"
                checked={caseInsensitive}
                onChange={(e) => onCaseInsensitiveChange(e.target.checked)}
              />
            }
            label="Aa"
            title="Case insensitive"
          />
          <FormControlLabel
            sx={toggleSx}
            control={
              <Checkbox
                size="small"
                checked={useRegex}
                onChange={(e) => onUseRegexChange(e.target.checked)}
              />
            }
            label=".*"
            title="Regular expression"
          />
        </>
      )}
      {compact && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, flexWrap: 'nowrap' }}>
          <FormControlLabel
            sx={toggleSx}
            control={
              <Checkbox
                size="small"
                checked={caseInsensitive}
                onChange={(e) => onCaseInsensitiveChange(e.target.checked)}
              />
            }
            label={<Typography variant="caption">Aa</Typography>}
            title="Case insensitive"
          />
          <FormControlLabel
            sx={toggleSx}
            control={
              <Checkbox
                size="small"
                checked={useRegex}
                onChange={(e) => onUseRegexChange(e.target.checked)}
              />
            }
            label={<Typography variant="caption">.*</Typography>}
            title="Regex"
          />
        </Box>
      )}
    </Box>
  );

  return content;
};

export default SearchBar;
